import { LockIcon, SparklesIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const ACCENT = "#ff6d00";
const NAVY = "#263238";

interface UpgradePromptProps {
  /** Feature key that was gated (from a 402 detail), if any. */
  feature?: string | null;
  currentPlan?: string | null;
  upgradeHint?: string | null;
  /** Override the headline message. */
  message?: string;
  compact?: boolean;
}

/** Graceful gated-feature card — shown in place of a locked feature, or in
 *  response to a 402. Never renders as a raw error. */
export function UpgradePrompt({
  feature,
  currentPlan,
  upgradeHint,
  message,
  compact = false,
}: UpgradePromptProps) {
  const headline =
    message ||
    (feature
      ? `"${feature}" isn't included in your plan`
      : "This feature requires a plan upgrade");
  const hint =
    upgradeHint ||
    "Upgrade your plan to unlock this capability. Contact your administrator.";
  return (
    <Card
      className="border-2"
      style={{ borderColor: ACCENT, background: "#f2f4f7" }}
    >
      <CardContent className={compact ? "p-3" : "p-6"}>
        <div className="flex items-start gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            style={{ background: ACCENT, color: "white" }}
          >
            <LockIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="font-semibold" style={{ color: NAVY }}>
              {headline}
            </h3>
            {currentPlan && (
              <p className="text-xs text-muted-foreground">
                Current plan: <span className="font-medium">{currentPlan}</span>
              </p>
            )}
            <p className="text-sm text-muted-foreground">{hint}</p>
            <Button
              size="sm"
              style={{ background: ACCENT, color: "white" }}
              onClick={() => {
                window.location.href = "/licensing";
              }}
            >
              <SparklesIcon className="mr-2 h-4 w-4" />
              View plans
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Pull the structured 402 payload off an axios error, if present. */
export function parseUpgrade402(err: unknown): {
  feature?: string;
  current_plan?: string;
  upgrade_hint?: string;
  message?: string;
} | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const detail = (err as any)?.response?.data?.detail;
  if (
    (err as any)?.response?.status === 402 &&
    detail &&
    typeof detail === "object"
  ) {
    return detail;
  }
  return null;
}
